import classNames from 'classnames';
import styled, { type DefaultTheme } from 'styled-components';
import { bounceKeyframes } from '../../style/animations';
import { actionColor, actionLight, colors } from '../../style';

export const sizes = {
  xxs: {
    width: '4px',
    height: '4px',
    margin: '1px',
  },
  xs: {
    width: '8px',
    height: '8px',
    margin: '2px',
  },
  sm: {
    width: '12px',
    height: '12px',
    margin: '3px',
  },
  md: {
    width: '18px',
    height: '18px',
    margin: '4px',
  },
  lg: {
    width: '24px',
    height: '24px',
    margin: '6px',
  },
};

type VariantResolver = (theme?: DefaultTheme) => string;

export const variants: Record<string, VariantResolver> = {
  white: () => colors.white,
  gray400: () => colors.gray400,
  gray900: () => colors.gray900,
  action: theme => actionColor({ theme }),
  actionLight: theme => actionLight({ theme }),
  inherit: () => 'inherit',
};

interface DotsLoaderProps {
  alignSelf?: 'center' | 'flex-end' | 'flex-start';
  size?: keyof typeof sizes;
  variant?: keyof typeof variants;
  justifyContent?: 'center' | 'flex-start' | 'flex-end' | 'space-between' | 'space-around';
  className?: string;
  absoluteCenter?: boolean;
}

const DotsLoaderWrapper = styled.div<DotsLoaderProps>`
  display: flex;
  justify-content: center;

  align-self: ${props => props.alignSelf};

  & > i {
    width: ${props => sizes[props.size || 'lg']?.width};
    height: ${props => sizes[props.size || 'lg']?.height};
    margin: ${props => sizes[props.size || 'lg']?.margin};
    border-radius: 50%;
    display: inline-block;
    background: ${props => variants[props.variant || 'gray400'](props.theme)};
    animation: ${bounceKeyframes} 800ms infinite ease-in-out both;

    &:last-child {
      margin-right: 0;
    }

    &:nth-child(1) {
      animation-delay: -320ms;
    }

    &:nth-child(2) {
      animation-delay: -160ms;
    }
  }

  &.absolute-center {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const DotsLoader = ({ className, absoluteCenter, variant = 'gray400', size = 'lg', ...rest }: DotsLoaderProps) => (
  <DotsLoaderWrapper
    variant={variant}
    aria-live="polite"
    aria-busy="true"
    role="alert"
    size={size}
    data-testid={'dots-loader-wrapper'}
    justifyContent={'center'}
    className={classNames({ 'absolute-center': absoluteCenter }, className)}
    {...rest}
  >
    <i />
    <i />
    <i />
  </DotsLoaderWrapper>
);

export default DotsLoader;
